const RangeIndicator = ({
  value = 2.5,
  ranges = { min: 0, low: 1.05, normal: 2.09, high: 2.8, max: 3.5 },
  sections = null,
  status = 'optimal',
  className = '',
  chartMaxLabel,
}) => {
  if (sections && Array.isArray(sections)) {
    const renderSectionsBasedIndicator = () => {
      const totalWidth = 100;
      const numSections = sections.length;
      const sectionWidth = totalWidth / numSections;
      const minValue = sections[0].min;
      const maxValue = sections[sections.length - 1].max;
      const chartMax = Number(chartMaxLabel) || maxValue;
      const valueNum = Number(value);
      let markerPosition = 0;

      if (valueNum <= minValue) {
        markerPosition = 0;
      } else if (valueNum >= chartMax) {
        markerPosition = totalWidth - 1;
      } else {
        let found = false;
        for (let i = 0; i < sections.length; i++) {
          const section = sections[i];
          if (valueNum >= section.min && valueNum <= section.max) {
            const sectionProgress =
              (valueNum - section.min) / (section.max - section.min);
            markerPosition = i * sectionWidth + sectionProgress * sectionWidth;
            found = true;
            break;
          }
        }

        if (!found) {
          for (let i = 0; i < sections.length - 1; i++) {
            const left = sections[i].max;
            const right = sections[i + 1].min;
            if (valueNum > left && valueNum < right) {
              const boundaryPos = (i + 1) * sectionWidth;
              markerPosition = boundaryPos + 0.1;
              found = true;
              break;
            }
          }
        }

        if (!found) {
          const ratio = (valueNum - minValue) / (chartMax - minValue);
          markerPosition = Math.max(
            0,
            Math.min(totalWidth - 1, ratio * totalWidth)
          );
        }
      }

      let markerColor = '#2B3133';
      let textColor = '#2B3133';
      for (const section of sections) {
        if (valueNum >= section.min && valueNum <= section.max) {
          if (section.color === 'grey') {
            markerColor = '#FF5959';
            textColor = '#FF5959';
          } else if (section.color === 'green') {
            markerColor = '#2B3133';
            textColor = '#2B3133';
          } else if (section.color === 'blue') {
            markerColor = '#2B3133';
            textColor = '#2B3133';
          }
          break;
        }
      }

      const getColorCode = (color) => {
        switch (color) {
          case 'grey':
            return '#E5E7EB';
          case 'blue':
            return '#42B1D0';
          case 'green':
            return '#119975';
          default:
            return '#E5E7EB';
        }
      };

      return (
        <div
          className={`w-full bg-[#FFF7F1] pb-6 pt-8 px-4 rounded-lg ${className}`}
        >
          <div className="relative flex flex-col items-center">
            <div
              className="absolute -top-6 transform -translate-x-1/2 text-sm font-poppins font-semibold"
              style={{ left: `${markerPosition}%`, color: textColor }}
            >
              {value}
            </div>

            <div
              className={`absolute w-[3px] h-5 -top-1 transform -translate-x-1/2 z-10`}
              style={{
                left: `${markerPosition}%`,
                backgroundColor: markerColor,
              }}
            />

            <div className="flex h-[10px] w-full rounded-full overflow-hidden">
              {sections.map((section, index) => (
                <div
                  key={index}
                  className="h-full"
                  style={{
                    width: `${sectionWidth}%`,
                    backgroundColor: getColorCode(section.color),
                  }}
                />
              ))}
            </div>

            <div className="relative w-full mt-2">
              {sections.map((section, index) => {
                if (index === sections.length - 1) return null;
                const position = (index + 1) * sectionWidth;
                const displayBoundary = sections[index + 1] ? sections[index + 1].min : section.max;
                return (
                  <div
                    key={index}
                    className="absolute text-xs font-poppins font-normal text-[#2B3133]"
                    style={{
                      left: `${position}%`,
                      transform: 'translateX(-50%)',
                    }}
                  >
                    <span>{ displayBoundary }</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    };

    return renderSectionsBasedIndicator();
  }

  const tailPercent = 5;
  const scalePercent = 100 - tailPercent;

  const equalSeg = scalePercent / 3;
  const totalGrey = equalSeg + tailPercent;
  const leftGrey = totalGrey / 2;
  const rightGrey = totalGrey / 2;

  const calculateMarkerPosition = (val, r) => {
    const v = Number(val);
    const { min, low, normal, max } = r;
    const chartMax = Number(chartMaxLabel) || max;

    if (v <= min) return 0;
    if (v >= chartMax) return leftGrey + 2 * equalSeg + (rightGrey - 1);

    if (v <= low) {
      const denom = low - min || 1;
      const t = (v - min) / denom;
      return t * leftGrey;
    }
    if (v <= normal) {
      const denom = normal - low || 1;
      const t = (v - low) / denom;
      return leftGrey + t * equalSeg;
    }
    if (v <= max) {
      const denom = max - normal || 1;
      const t = (v - normal) / denom;
      return leftGrey + equalSeg + t * equalSeg;
    }
    const denom = chartMax - max || 1;
    const t = (v - max) / denom;
    return leftGrey + 2 * equalSeg + t * rightGrey;
  };

  const markerPosition = calculateMarkerPosition(Number(value), ranges);

  const markerColor = status === 'attention' ? '#FF5959' : '#2B3133';

  const seg1 = leftGrey;
  const seg2 = equalSeg;
  const seg3 = equalSeg;
  const seg4 = rightGrey;

  return (
    <div
      className={`w-full bg-[#FFF7F1] pb-6 pt-8 px-4 rounded-lg ${className}`}
    >
      <div className="relative flex flex-col items-center">
        <div
          className="absolute -top-6 transform -translate-x-1/2 text-sm font-poppins font-semibold text-[#2B3133]"
          style={{ left: `${markerPosition}%` }}
        >
          {value}
        </div>

        <div
          className={`absolute w-[3px] h-5 -top-1 transform -translate-x-1/2 z-10`}
          style={{ left: `${markerPosition}%`, backgroundColor: markerColor }}
        />

        <div className="flex h-[10px] w-full rounded-full overflow-hidden">
          <div className="h-full bg-[#D9D9D9]" style={{ width: `${seg1}%` }} />
          <div className="h-full bg-[#71C0D6]" style={{ width: `${seg2}%` }} />
          <div className="h-full bg-[#119975]" style={{ width: `${seg3}%` }} />
          <div className="h-full bg-[#D9D9D9]" style={{ width: `${seg4}%` }} />
        </div>

        <div className="relative w-full mt-2">
          <div
            className="absolute text-xs font-poppins font-normal text-[#2B3133]"
            style={{ left: `${seg1}%`, transform: 'translateX(-50%)' }}
          >
            <span>{ranges.low}</span>
          </div>
          <div
            className="absolute text-xs font-poppins font-normal text-[#2B3133]"
            style={{ left: `${seg1 + seg2}%`, transform: 'translateX(-50%)' }}
          >
            <span>{ranges.normal ?? ranges.high}</span>
          </div>
          <div
            className="absolute text-xs font-poppins font-normal text-[#2B3133]"
            style={{
              left: `${seg1 + seg2 + seg3}%`,
              transform: 'translateX(-50%)',
            }}
          >
            <span>{ranges.optimalMax ?? ranges.max}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RangeIndicator;